/** OS permission as reported by @capacitor/push-notifications; null when unreadable. */
export type NativePushPermission =
  | 'prompt'
  | 'prompt-with-rationale'
  | 'granted'
  | 'denied'
  | null;

export type NativePushState =
  | 'unknown' // still reading the OS / profile — say nothing rather than guess
  | 'denied' // OS permission denied; on Android 13+ the dialog no longer appears
  | 'prompt' // never granted — one system dialog away
  | 'no-token-on-server' // permission is fine, but the backend has no token from this device
  | 'ok';

/**
 * Whether push can actually reach this device — the app's counterpart to BrowserPushStatus.
 *
 * The registration token, not the permission, is what decides delivery: dismissing the
 * onboarding with "Later" leaves the permission untouched (and on Android 12 there is no
 * runtime permission at all — it always reads as granted) while no token is ever sent to the
 * backend, so nothing arrives and every screen still looks normal. That is why the token check
 * comes first and the permission only picks the wording and the button.
 *
 * `serverTokens === null` means the profile hasn't loaded yet, `permission === undefined` that
 * the plugin hasn't answered yet — both stay 'unknown' so a slow load never flashes a warning.
 */
export const detectNativePushState = ({
  permission,
  systemNotificationsEnabled,
  localToken,
  serverTokens,
}: {
  permission: NativePushPermission | undefined;
  /** NotificationManagerCompat.areNotificationsEnabled(); undefined when unreadable. */
  systemNotificationsEnabled?: boolean;
  localToken: string | null;
  serverTokens: readonly string[] | null;
}): NativePushState => {
  if (permission === undefined || serverTokens === null) {
    return 'unknown';
  }
  // The system switch outranks the permission: below Android 13 the permission always reads
  // 'granted', so switching notifications off in settings is invisible to it.
  if (permission === 'denied' || systemNotificationsEnabled === false) {
    return 'denied';
  }
  // `fcm_token` mirrors the token this device last registered; it counts only while the
  // backend still lists it — after a sign-in as somebody else the row moves to that account.
  if (localToken && serverTokens.includes(localToken)) {
    return 'ok';
  }
  // A null permission means the plugin couldn't be read, not that it was refused — treat it
  // like granted so we point at the missing token instead of asking for a dialog that the
  // OS may never show.
  if (permission === 'granted' || permission === null) {
    return 'no-token-on-server';
  }
  return 'prompt';
};
